import { useContext, useState, useRef, useMemo, useCallback } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Popper from '@mui/material/Popper';
import type { State as PopperState } from '@popperjs/core';
import type { SurveyQuestionType } from '@trinity/chat/enums';
import type { SurveyFiltering, SurveyQuestionOption } from '@trinity/chat/types/survey';
import { Checkbox } from '@trinity/chat-components/components/presentation/Checkbox.js';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context.js';
import { cx } from '@trinity/chat-components/utils';
import type { CheckboxGroupItem, CheckboxGroupOnItemToggleItem, CheckboxGroupProps } from '@/components/presentation/CheckboxGroup';
import { ProjectResponseFilterContext } from '@/containers/GroupProject/Context';
import { parseSurveyRichText } from '@/containers/Survey/utils';
import { CommonRow } from './CommonRow';
import { useFilterQuestion } from './hooks';
import { Selector } from './Selector';
import styles from './style/SurveyResponseFilter.module.css';
import type { FilterProps, OrderedFilter } from './utils';

// Custom CheckboxGroup that supports theming
type ThemedCheckboxGroupProps = CheckboxGroupProps & {
  checkedColor?: string;
};

function ThemedCheckboxGroup({ className, checkedItems, items, onToggle, onInputChange, checkedColor }: ThemedCheckboxGroupProps) {
  const renderItem = useCallback((item: CheckboxGroupItem, index: number) => {
    const onChange = () => onToggle({ id: item.id });
    const checked = checkedItems.includes(item.id);

    const style: React.CSSProperties = {
      marginBottom: index === items.length - 1 ? 0 : 15,
      alignItems: 'flex-start',
    };

    return (
      <ThemedCheckboxLabel
        key={item.id}
        checked={checked}
        label={item.label}
        onChange={onChange}
        disabled={item.disabled}
        style={style}
        checkedColor={checkedColor} />
    );
  }, [checkedItems, items.length, onToggle, checkedColor]);

  return (
    <FormGroup className={className}>
      {items.map(renderItem)}
    </FormGroup>
  );
}

// Custom CheckboxLabel that supports theming
type ThemedCheckboxLabelProps = {
  className?: string;
  label?: string;
  checked?: boolean;
  disabled?: boolean;
  onChange?: () => void;
  style?: React.CSSProperties;
  checkedColor?: string;
};

function ThemedCheckboxLabel({
  className,
  label = '',
  checked = false,
  disabled = false,
  onChange = () => {},
  style,
  checkedColor,
}: ThemedCheckboxLabelProps) {
  return (
    <FormControlLabel
      className={className}
      sx={{
        marginLeft: 0,
        [`& .MuiFormControlLabel-label`]: {
          fontSize: 16,
          fontFamily: 'var(--font-reg)',
          width: '100%',
        },
      }}
      control={
        <Checkbox
          className={checked ? styles.checked : styles.unchecked}
          checked={checked}
          style={{
            marginRight: 10,
          }}
          disabled={disabled}
          onChange={onChange} />
        // checkedColor={checkedColor} />
      }
      label={label}
      style={style} />
  );
}

export const MultiChoiceFilter = ({ filterEntry, filterActions }: FilterProps<SurveyQuestionType.MultipleChoice>) => {
  const filterContext = useContext(ProjectResponseFilterContext);
  const appThemingContext = useContext(AppThemingContext);

  const [isOpen, setOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState(filterEntry.filter?.optionIds || []);
  const [optionsIsSelected, setOptionsIsSelected] = useState<boolean>(filterEntry.filter ? filterEntry.filter.isSelected : true);

  const question = useFilterQuestion(filterEntry);

  const options: SurveyQuestionOption[] = useMemo(() => {
    return question.options;
  }, [question]);

  function onCheckboxToggle(item: CheckboxGroupOnItemToggleItem) {
    let newItems: number[] = [];
    if (selectedOptions.includes(+item.id)) {
      newItems = selectedOptions.filter(o => o !== +item.id);
    } else {
      newItems = [...selectedOptions, +item.id];
    }

    setSelectedOptions(newItems);

    passbackFilter({ optionIds: newItems });
  }

  function onIsSelectedChange(val: boolean) {
    setOptionsIsSelected(val);
    passbackFilter({ isSelected: val });
  }

  function passbackFilter(partialFilter?: Partial<SurveyFiltering.QuestionFilter<SurveyQuestionType.MultipleChoice>>) {
    const constructedFilter: OrderedFilter<SurveyQuestionType.MultipleChoice> = {
      ...filterEntry,
      filter: {
        ...{
          type: 'option-filter',
          optionIds: selectedOptions,
          isSelected: optionsIsSelected,
        }, ...partialFilter,
      },
    };

    filterActions.changeFilter(constructedFilter);
  }

  function clearAll() {
    setSelectedOptions([]);
    passbackFilter({ optionIds: [] });
  }

  function selectAll() {
    const allOptions = options.map(o => o.base.id);
    setSelectedOptions(allOptions);
    passbackFilter({ optionIds: allOptions });
  }

  const checkboxItems = options.map<CheckboxGroupItem>(o => ({
    label: parseSurveyRichText(o.value),
    id: o.base.id,
  }));

  const buttonRef = useRef(undefined);
  const displayText = selectedOptions.length
    ? selectedOptions.map(o1 => {
      const option = options.find(o2 => o2.base.id == o1);
      if (!option) return '';
      return parseSurveyRichText(option.value);
    }).filter(Boolean).join(', ')
    : 'Select a value'
    ;

  const checkedColor = appThemingContext?.enabled ? appThemingContext?.palette?.primary?.main : undefined;

  return (
    <CommonRow filterEntry={filterEntry} filterActions={filterActions}>
      <Selector
        items={[true, false]}
        displayValue={v => v ? 'Selected' : 'Not Selected'}
        onSelect={onIsSelectedChange}
        value={optionsIsSelected} />
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <div className={styles.filterRowInput}>
          <div
            className={styles.selectorButton}
            ref={buttonRef}
            onClick={e => setOpen(!isOpen)}>
            {displayText}
          </div>
          <Popper
            open={isOpen}
            anchorEl={buttonRef.current}
            className={cx(styles.selectorPopover, styles.multichoicePopover)}
            placement={'bottom-start'}
            popperOptions={{
              modifiers: [
                {
                  name: 'update-width',
                  phase: 'afterWrite',
                  enabled: true,
                  fn({ state }: { state: PopperState }) {
                    state.elements.popper.style.width = `${(state.elements.reference as HTMLElement).offsetWidth}px`;
                  },
                },
              ],
              onFirstUpdate: state => {
                state.elements.popper.style.width = `${(state.elements.reference as HTMLElement).offsetWidth}px`;
              },
            }}>
            <div className={styles.multichoiceToggleAll}>
              <span
                style={{ marginRight: '10px' }}
                onClick={selectAll}>
                Select All
              </span>
              <span onClick={clearAll}>Clear All</span>
            </div>
            <div className={styles.checkboxGroupContainer}>
              <ThemedCheckboxGroup
                className={styles.checkboxGroup}
                items={checkboxItems}
                checkedItems={selectedOptions}
                onToggle={onCheckboxToggle}
                checkedColor={checkedColor} />
            </div>
          </Popper>
        </div>
      </ClickAwayListener>
    </CommonRow>
  );
};